import React, { useState } from "react";
import { Button, Drawer, Input, Modal } from "antd";
import Icon from "@mdi/react";
import { mdiClose } from "@mdi/js";
import useBreakpoint from "../../../../utils/useBreakpoint";

const Head = ({ title, onClose }: { title: string; onClose: () => void }) => (
  <div className="gt-dialog__head"><h3>{title}</h3><button type="button" onClick={onClose} aria-label="Close"><Icon path={mdiClose} size="22px" /></button></div>
);

// Small centred confirmation used by "Change Agent?" and "Sign In Required" (Figma 8821:24661 / 8821:24640).
function Confirm({ open, title, children, cancelText = "Cancel", okText, onOk, onCancel }: {
  open: boolean; title: string; children: React.ReactNode; cancelText?: string; okText: string; onOk: () => void; onCancel: () => void;
}) {
  return (
    <Modal open={open} onCancel={onCancel} footer={null} centered closable={false} width={616} wrapClassName="gt-dialog is-small" destroyOnClose>
      <Head title={title} onClose={onCancel} />
      <p className="gt-dialog__text">{children}</p>
      <div className="gt-dialog__actions">
        <Button className="gt-btn-outline" onClick={onCancel}>{cancelText}</Button>
        <Button type="primary" onClick={onOk}>{okText}</Button>
      </div>
    </Modal>
  );
}

export function ChangeAgentConfirm({ open, picking, onOk, onCancel }: { open: boolean; picking: boolean; onOk: () => void; onCancel: () => void }) {
  return (
    <Confirm open={open} title="Change Agent?" okText="Change Agent" onOk={onOk} onCancel={onCancel}>
      {picking
        ? "Your booking will continue with the newly selected agent."
        : "Once you change your agent, you’ll need to select another agent to continue with this booking."}
    </Confirm>
  );
}

interface ReasonsProps {
  open: boolean;
  reasons: { value: string; label: string }[];
  submitting: boolean;
  onCancel: () => void;
  onSubmit: (reason: string, other: string) => void;
}

// "Change Agent?" reasons form (Figma 8821:25843 desktop dialog, 10308:317776 mobile sheet): radio rows plus "Other".
export function ReasonsDialog({ open, reasons, submitting, onCancel, onSubmit }: ReasonsProps) {
  const isMobile = useBreakpoint();
  const [reason, setReason] = useState("");
  const [other, setOther] = useState("");
  const needsOther = reason === "others";
  const canSubmit = !!reason && (!needsOther || !!other.trim());

  const body = (
    <>
      <p className="gt-dialog__text">Please let us know why you’d like to change your assigned agent.</p>
      <div className="gt-reasons" role="radiogroup" aria-label="Reason">
        {reasons.map((item) => (
          <button type="button" role="radio" aria-checked={reason === item.value} key={item.value} className={`gt-reason ${reason === item.value ? "is-selected" : ""}`} onClick={() => setReason(item.value)}>
            <i /><span>{item.label}</span>
          </button>
        ))}
      </div>
      {needsOther && <Input.TextArea rows={4} value={other} onChange={(event) => setOther(event.target.value)} placeholder="Share your reason" className="gt-reasons__other" />}
    </>
  );

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onClose={onCancel}
        width="100%"
        closable={false}
        destroyOnClose
        rootClassName="gt-passenger-drawer gt-m-sheet"
        title={<div className="gt-drawer__head"><span>Change Agent?</span><button type="button" onClick={onCancel} aria-label="Close"><Icon path={mdiClose} size="22px" /></button></div>}
        footer={<div className="gt-drawer__footer is-mobile"><Button type="primary" block loading={submitting} disabled={!canSubmit} onClick={() => onSubmit(reason, other)}>Submit</Button></div>}
      >
        {body}
      </Drawer>
    );
  }
  return (
    <Modal open={open} onCancel={onCancel} footer={null} centered closable={false} width={791} wrapClassName="gt-dialog" destroyOnClose>
      <h3 className="gt-dialog__title">Change Agent?</h3>
      {body}
      <div className="gt-dialog__actions">
        <Button className="gt-btn-outline" onClick={onCancel}>Cancel</Button>
        <Button type="primary" loading={submitting} disabled={!canSubmit} onClick={() => onSubmit(reason, other)}>Submit</Button>
      </div>
    </Modal>
  );
}
